import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { X, Heart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PhysicalCondition, TreatmentRecord } from '../../../types/treatment';

interface PhysicalConditionSectionProps {
  record: Partial<TreatmentRecord>;
  onChange: (updates: Partial<TreatmentRecord>) => void;
  mode?: 'view' | 'edit' | 'create';
}

const physicalConditionOptions: { value: PhysicalCondition; label: string; color: string }[] = [
  { value: '良好', label: '良好', color: 'success.main' },
  { value: '普通', label: '普通', color: 'info.main' },
  { value: '疲れ気味', label: '疲れ気味', color: 'warning.main' },
  { value: '不調', label: '不調', color: 'error.main' },
];

const commonSymptoms = [
  '肩こり', '腰痛', '頭痛', '眼精疲労', '不眠', 
  '冷え性', 'むくみ', '疲労感', 'ストレス', '便秘',
];

const commonAllergies = [
  '花粉症', 'ハウスダスト', '食物アレルギー', '金属アレルギー', 
  'アトピー性皮膚炎', '薬物アレルギー',
];

export const PhysicalConditionSection: React.FC<PhysicalConditionSectionProps> = ({
  record,
  onChange,
  mode = 'view',
}) => {
  const [customSymptom, setCustomSymptom] = useState('');
  const [customAllergy, setCustomAllergy] = useState('');

  const handleFieldChange = (field: keyof TreatmentRecord, value: unknown) => {
    onChange({ [field]: value });
  };

  const toggleArrayItem = (field: keyof TreatmentRecord, item: string) => {
    const currentArray = (record[field] as string[]) || [];
    const newArray = currentArray.includes(item)
      ? currentArray.filter(i => i !== item)
      : [...currentArray, item];
    handleFieldChange(field, newArray);
  };

  const addCustomItem = (field: keyof TreatmentRecord, value: string, setValue: (v: string) => void) => {
    if (!value.trim()) return;
    const currentArray = (record[field] as string[]) || [];
    if (!currentArray.includes(value.trim())) {
      handleFieldChange(field, [...currentArray, value.trim()]);
    }
    setValue('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Heart className="h-5 w-5" />
          身体状態
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4">
          {/* 体調 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">体調</label>
              <Select
                value={record.physicalCondition || '普通'}
                onValueChange={(value) => handleFieldChange('physicalCondition', value)}
                disabled={mode === 'view'}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {physicalConditionOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          "w-3 h-3 rounded-full",
                          option.value === '良好' && "bg-green-500",
                          option.value === '普通' && "bg-blue-500",
                          option.value === '疲れ気味' && "bg-yellow-500",
                          option.value === '不調' && "bg-red-500"
                        )} />
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 症状・お悩み */}
            <div className="space-y-2">
              <label className="text-sm font-medium">症状・お悩み</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {commonSymptoms.map((symptom) => (
                  <Badge
                    key={symptom}
                    variant={(record.symptoms || []).includes(symptom) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => mode !== 'view' && toggleArrayItem('symptoms', symptom)}
                  >
                    {symptom}
                  </Badge>
                ))}
              </div>
              {mode !== 'view' && (
                <div className="flex gap-2">
                  <Input
                    value={customSymptom}
                    onChange={(e) => setCustomSymptom(e.target.value)}
                    placeholder="カスタム入力"
                    className="text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addCustomItem('symptoms', customSymptom, setCustomSymptom)}
                  />
                  <Button
                    size="sm"
                    onClick={() => addCustomItem('symptoms', customSymptom, setCustomSymptom)}
                  >
                    追加
                  </Button>
                </div>
              )}
              {(record.symptoms || []).some(s => !commonSymptoms.includes(s)) && (
                <div className="flex flex-wrap gap-2">
                  {(record.symptoms || []).filter(s => !commonSymptoms.includes(s)).map((symptom) => (
                    <Badge
                      key={symptom}
                      variant="secondary"
                      className="cursor-pointer"
                    >
                      {symptom}
                      {mode !== 'view' && (
                        <X
                          className="ml-1 h-3 w-3"
                          onClick={() => toggleArrayItem('symptoms', symptom)}
                        />
                      )}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* スライダーセクション */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">痛みレベル: {record.painLevel || 0}</label>
              <Slider
                value={[record.painLevel || 0]}
                onValueChange={(value) => handleFieldChange('painLevel', value[0])}
                disabled={mode === 'view'}
                min={0}
                max={10}
                step={1}
                className="mt-2"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">ストレスレベル: {record.stressLevel || 0}</label>
              <Slider
                value={[record.stressLevel || 0]}
                onValueChange={(value) => handleFieldChange('stressLevel', value[0])}
                disabled={mode === 'view'}
                min={0}
                max={10}
                step={1}
                className="mt-2"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">睡眠の質: {record.sleepQuality || 0}</label>
              <Slider
                value={[record.sleepQuality || 0]}
                onValueChange={(value) => handleFieldChange('sleepQuality', value[0])}
                disabled={mode === 'view'}
                min={0}
                max={10}
                step={1}
                className="mt-2"
              />
            </div>
          </div>

          {/* テキストエリアセクション */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">既往歴</label>
              <Textarea
                value={record.medicalHistory || ''}
                onChange={(e) => handleFieldChange('medicalHistory', e.target.value)}
                disabled={mode === 'view'}
                rows={3}
                placeholder="過去の病気・手術歴など"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">アレルギー</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {commonAllergies.map((allergy) => (
                  <Badge
                    key={allergy}
                    variant={(record.allergies || []).includes(allergy) ? "destructive" : "outline"}
                    className="cursor-pointer"
                    onClick={() => mode !== 'view' && toggleArrayItem('allergies', allergy)}
                  >
                    {allergy}
                  </Badge>
                ))}
              </div>
              {mode !== 'view' && (
                <div className="flex gap-2">
                  <Input
                    value={customAllergy}
                    onChange={(e) => setCustomAllergy(e.target.value)}
                    placeholder="カスタム入力"
                    className="text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addCustomItem('allergies', customAllergy, setCustomAllergy)}
                  />
                  <Button
                    size="sm"
                    onClick={() => addCustomItem('allergies', customAllergy, setCustomAllergy)}
                  >
                    追加
                  </Button>
                </div>
              )}
              {(record.allergies || []).some(a => !commonAllergies.includes(a)) && (
                <div className="flex flex-wrap gap-2">
                  {(record.allergies || []).filter(a => !commonAllergies.includes(a)).map((allergy) => (
                    <Badge
                      key={allergy}
                      variant="destructive"
                      className="cursor-pointer"
                    >
                      {allergy}
                      {mode !== 'view' && (
                        <X
                          className="ml-1 h-3 w-3"
                          onClick={() => toggleArrayItem('allergies', allergy)}
                        />
                      )}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};